/**
 * Redux container for AgentScannersView component
 *
 * This container connects the AgentScannersView presentational component to the Redux store
 * using React-Redux's connect() HOC with a custom RootStoreContext.
 *
 * @see AgentScannersView for the presentational component
 */
import React from 'react';
import { connect } from 'react-redux';
import SuccessFilledIcon from '@mulesoft/anypoint-icons/lib/svg/success-filled.svg';
import SpinnerIcon from '@mulesoft/anypoint-icons/lib/svg/spinner.svg';
import RootStoreContext from '~/contexts/RootStoreContext';
import { actions as modalActions } from '~/domains/modal';
import {
  selectors as agentScannersSelectors,
  actions as agentScannersActions
} from '~/domains/agentScanners';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import {
  getActiveOrganizationId,
  getActiveOrganization
} from '~/utils/organization';
import { escapeHtml } from '../../utils/utils';
import AgentScannersView from './AgentScannersView';

/**
 * Maps Redux state to component props
 */
const mapStateToProps = (state) => {
  const profile = commonSelectors.profile(state);
  const activeOrganizationId = getActiveOrganizationId(profile);

  return {
    scanConfigurations: agentScannersSelectors.scanners(state),
    isFetchingScanConfigurations:
      agentScannersSelectors.isFetchingScanners(state),
    organizationId: activeOrganizationId,
    organizationName:
      getActiveOrganization(profile)?.name || profile?.organization?.name,
    ownerId: profile?.id,
    context: commonSelectors.context(state),
    agentCapacity: agentScannersSelectors.agentCapacity(state),
    isManualReviewEnabled: agentScannersSelectors.isManualReviewEnabled(state)
  };
};

/**
 * Maps Redux dispatch actions to component props
 */
const mapDispatchToProps = (dispatch) => ({
  openModal: ({ modalType, modalProps }) =>
    dispatch(modalActions.openModal({ modalType, modalProps })),

  /**
   * Creates a new scanner and shows success alert.
   * @param {string} organizationId - The organization ID
   * @param {Object} scannerData - Scanner data from the create form
   * @param {string} [scannerData.name] - Scanner name (validated with optional chaining + fallback)
   */
  onCreateScanner: (organizationId, scannerData, onFormClose) => {
    const safeName = escapeHtml(scannerData?.name || 'Unknown');

    if (onFormClose) {
      onFormClose();
    }

    dispatch(
      commonActions.setAlert({
        type: 'info',
        icon: React.createElement(
          'span',
          {
            role: 'status',
            'aria-label': 'Creating scanner',
            style: {
              display: 'inline-flex',
              animation: 'spin 1s linear infinite'
            }
          },
          React.createElement(SpinnerIcon)
        ),
        message: `Creating ${safeName}...`,
        isHTML: false
      })
    );

    dispatch(
      agentScannersActions.createAgentScanner(organizationId, scannerData),
      {
        skipErrorHandler: () => true
      }
    )
      .then((createdScanner) => {
        const scannerId = createdScanner?.id;

        if (!scannerId) {
          dispatch(
            commonActions.setAlert({
              type: 'success',
              icon: React.createElement(SuccessFilledIcon),
              message: `Scanner '${safeName}' was created.`,
              isHTML: false
            })
          );

          return;
        }

        const runNowKey = `runNow_${scannerId.replace(/-/g, '_')}`;

        const handleRunNow = () => {
          if (!window[runNowKey]) return;
          delete window[runNowKey];
          dispatch(commonActions.closeAlert());

          dispatch(
            agentScannersActions.runScanConfiguration(organizationId, scannerId)
          ).then(() => {
            dispatch(
              agentScannersActions.loadAgentScanners(organizationId)
            ).then(() => {
              const abortFunctionKey = `abortScan_${scannerId.replace(/-/g, '_')}`;

              const cleanup = () => {
                if (window[abortFunctionKey]) {
                  delete window[abortFunctionKey];
                }
              };

              const handleAbort = () => {
                if (!window[abortFunctionKey]) return;
                cleanup();

                dispatch(
                  agentScannersActions.abort(organizationId, scannerId)
                ).then(() => {
                  dispatch(commonActions.closeAlert());
                  dispatch(
                    commonActions.setAlert({
                      type: 'info',
                      message: `Your '${safeName}' scan is being aborted.`,
                      isHTML: false
                    })
                  );

                  if (organizationId) {
                    dispatch(
                      agentScannersActions.loadAgentScanners(organizationId)
                    );
                  }
                });
              };

              window[abortFunctionKey] = handleAbort;
              const safeAbortKey = escapeHtml(abortFunctionKey);

              dispatch(
                commonActions.setAlert({
                  type: 'info',
                  message:
                    `Your '${safeName}' Scanner is currently running. ` +
                    `Click <a href="javascript:void(0)" ` +
                    `onclick="if(window['${safeAbortKey}']){window['${safeAbortKey}']();}" ` +
                    `style="color:inherit;text-decoration:underline;cursor:pointer;font-weight:600;">` +
                    `Abort</a> if you need to stop it now.`,
                  isHTML: true
                })
              );
            });
          });
        };

        window[runNowKey] = handleRunNow;
        const safeRunNowKey = escapeHtml(runNowKey);

        dispatch(
          commonActions.setAlert({
            type: 'success',
            icon: React.createElement(SuccessFilledIcon),
            message:
              `'${safeName}' was created. ` +
              `<a href="javascript:void(0)" ` +
              `onclick="if(window['${safeRunNowKey}']){window['${safeRunNowKey}']();}" ` +
              `style="color:inherit;text-decoration:underline;cursor:pointer;font-weight:600;">` +
              `Run Now</a>`,
            isHTML: true
          })
        );
      })
      .catch(() => {
        dispatch(
          commonActions.setAlert({
            type: 'error',
            message: `Failed to create scanner '${safeName}'. Please try again.`,
            isHTML: false
          })
        );
      });
  },

  /**
   * Updates an existing scanner and shows success alert.
   * @param {string} organizationId - The organization ID
   * @param {string} scannerId - The scanner ID to update
   * @param {Object} scannerData - Scanner data from the edit form
   * @param {string} [scannerData.name] - Scanner name (validated with optional chaining + fallback)
   */
  onUpdateScanner: (organizationId, scannerId, scannerData, onFormClose) => {
    const safeName = escapeHtml(scannerData?.name || 'Unknown');

    if (onFormClose) {
      onFormClose();
    }

    dispatch(
      commonActions.setAlert({
        type: 'info',
        icon: React.createElement(
          'span',
          {
            role: 'status',
            'aria-label': 'Updating scanner',
            style: {
              display: 'inline-flex',
              animation: 'spin 1s linear infinite'
            }
          },
          React.createElement(SpinnerIcon)
        ),
        message: `Updating ${safeName}...`,
        isHTML: false
      })
    );

    dispatch(
      agentScannersActions.updateAgentScanner(
        organizationId,
        scannerId,
        scannerData
      ),
      {
        skipErrorHandler: () => true
      }
    )
      .then(() => {
        dispatch(
          commonActions.setAlert({
            type: 'success',
            icon: React.createElement(SuccessFilledIcon),
            message: `Scanner '${safeName}' was updated.`,
            isHTML: false
          })
        );
      })
      .catch(() => {
        dispatch(
          commonActions.setAlert({
            type: 'error',
            message: `Failed to update scanner '${safeName}'. Please try again.`,
            isHTML: false
          })
        );
      });
  },
  onLoadScanners: (organizationId) =>
    dispatch(agentScannersActions.loadAgentScanners(organizationId)),
  onLoadAgentCapacity: (organizationId) =>
    dispatch(agentScannersActions.loadAgentCapacity(organizationId))
});

/**
 * Connected component with Redux state and actions
 * Uses custom RootStoreContext for store access
 */
export default connect(mapStateToProps, mapDispatchToProps, null, {
  context: RootStoreContext
})(AgentScannersView);
